import React from 'react';
import { X, Plus, ArrowUp, ArrowDown, Video } from 'lucide-react';
import { convertToEmbedUrl } from '../../utils/videoUrlConverter';

interface MediaEntry {
  id: string;
  url: string;
  type: 'image' | 'video';
  credit: string;
  credit_url: string;
}

interface EditableMediaListProps {
  entries: MediaEntry[];
  onChange: (entries: MediaEntry[]) => void;
}

const EditableMediaList = ({ entries, onChange }: EditableMediaListProps) => {
  const generateId = () => `entry-${Math.random().toString(36).substr(2, 9)}`;

  const addEntry = () => {
    onChange([
      ...entries,
      { 
        id: generateId(),
        url: '', 
        type: 'image', 
        credit: '', 
        credit_url: '' 
      }
    ]);
  };

  const removeEntry = (index: number) => {
    onChange(entries.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, field: keyof Omit<MediaEntry, 'id'>, value: string) => {
    const newEntries = [...entries];
    
    // If updating URL field and it's a video type, convert YouTube URLs
    if (field === 'url' && newEntries[index].type === 'video') {
      value = convertToEmbedUrl(value);
    }
    
    newEntries[index] = { ...newEntries[index], [field]: value };
    onChange(newEntries);
  };

  const moveEntry = (index: number, direction: 'up' | 'down') => {
    const newEntries = [...entries];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < entries.length) {
      [newEntries[index], newEntries[newIndex]] = [newEntries[newIndex], newEntries[index]];
      onChange(newEntries);
    }
  };

  const renderPreview = (entry: MediaEntry) => {
    if (!entry.url) return null;

    if (entry.type === 'image') {
      return (
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-background-secondary">
          <img
            src={entry.url}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      );
    }

    return (
      <div className="w-20 h-20 rounded-lg bg-background-secondary flex items-center justify-center">
        <Video className="w-8 h-8 text-content-secondary" />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {entries.map((entry, index) => (
        <div
          key={entry.id}
          className="bg-background-secondary rounded-lg p-4 border border-gray-800"
        >
          <div className="flex items-start gap-4">
            {/* Move Buttons */}
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => moveEntry(index, 'up')}
                disabled={index === 0}
                className="p-1 text-content-secondary hover:text-brand disabled:opacity-50 disabled:hover:text-content-secondary"
                title="Move up"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => moveEntry(index, 'down')}
                disabled={index === entries.length - 1}
                className="p-1 text-content-secondary hover:text-brand disabled:opacity-50 disabled:hover:text-content-secondary"
                title="Move down"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
            </div>

            {/* Preview */}
            {renderPreview(entry)}

            {/* Fields */}
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Media URL *
                  </label>
                  <input
                    type="url"
                    value={entry.url}
                    onChange={(e) => updateEntry(index, 'url', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-gray-800 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Type *
                  </label>
                  <select
                    value={entry.type}
                    onChange={(e) => updateEntry(index, 'type', e.target.value as 'image' | 'video')}
                    className="w-full px-3 py-2 bg-background border border-gray-800 rounded-lg"
                    required
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Credit
                  </label>
                  <input
                    type="text"
                    value={entry.credit}
                    onChange={(e) => updateEntry(index, 'credit', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Credit URL
                  </label>
                  <input
                    type="url"
                    value={entry.credit_url}
                    onChange={(e) => updateEntry(index, 'credit_url', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-gray-800 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Remove Button */}
            <button
              type="button"
              onClick={() => removeEntry(index)}
              className="text-content-secondary hover:text-red-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addEntry}
        className="flex items-center gap-2 px-4 py-2 bg-background border border-gray-800 rounded-lg text-content-secondary hover:text-content-primary"
      >
        <Plus className="w-4 h-4" />
        Add Media Entry
      </button>
    </div>
  );
};

export default EditableMediaList;